import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { PatientForm } from './PatientForm'
import { usePatient } from '../hooks/usePatient'
import { useUpdatePatient } from '../hooks/useUpdatePatient'
import { toast } from '@/hooks/use-toast'

interface EditPatientDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  patientId: string
}

export const EditPatientDialog: React.FC<EditPatientDialogProps> = ({ 
  open, 
  onOpenChange, 
  patientId 
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const { data: patient, isLoading: isLoadingPatient } = usePatient(patientId)
  const { mutateAsync: updatePatient } = useUpdatePatient()

  const handleSubmit = async (data: any) => {
    setIsLoading(true)
    try {
      await updatePatient({ id: patientId, ...data })
      toast({
        title: 'Success',
        description: 'Patient information has been successfully updated.',
      })
      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update patient information. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingPatient) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[800px]">
          <div className="flex items-center justify-center h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  if (!patient) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Edit Patient Information</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <PatientForm 
            patient={patient}
            onSubmit={handleSubmit}
            onCancel={() => onOpenChange(false)}
            isLoading={isLoading}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
